import React, { useState } from "react";
import classNames from "classnames";
import gridTableStyles from "@design-system/Tables/GridTable/GridTableBase.module.scss";
import { ProtectedPeriodsWarning } from "@common/dto/financialAccount/mergeFinancialAccounts/merge-financial-accounts-response.dto";
import { Transaction } from "@common/types/transaction";
import { pluralize } from "@common/helpers/string";
import { StandardModalBody } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ReconciliationRanges } from "@components/reconciliation/ReconciliationRanges/ReconciliationRanges";
import { formatIsoDateForDisplay } from "@helpers/date";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { TransactionToMerge } from "./TransactionToMerge";
import tableStyles from "./DeduplicatedTransactionsPreview.module.scss";

interface Props {
    warning: ProtectedPeriodsWarning;
    lockedTransactions: Transaction[];
    lockedTransactionsNumber: number;
    destinationAccountName: string;
    mergedAccountName: string;
    onCancel: () => void;
    onContinue: () => Promise<void>;
}

export const ProtectedPeriodsWarningModal: React.FC<Props> = ({
    warning,
    lockedTransactions,
    lockedTransactionsNumber,
    destinationAccountName,
    mergedAccountName,
    onCancel,
    onContinue,
}) => {
    const [continuing, setContinuing] = useState(false);
    const canEditLocks = useCanPerformWorkspaceAction(
        WorkspaceAction.LOCKS_EDIT,
    );
    const requiresUnlock = lockedTransactionsNumber > 0;
    const isUnlockBlocked = requiresUnlock && !canEditLocks;

    return (
        <StandardModalBody>
            <h4>This merge affects protected periods</h4>
            <p className="mb-0">
                Some transactions fall inside reconciled or locked periods.
                Merging will move them anyway, which can change already
                finalized numbers. Review before continuing.
            </p>
            {warning.lockDate && (
                <p className="mb-0 mt-2">
                    Books are locked on or before{" "}
                    <strong>{formatIsoDateForDisplay(warning.lockDate)}</strong>
                    .
                </p>
            )}
            <ReconciliationRanges
                title={destinationAccountName}
                ranges={warning.destinationReconciliationRanges}
            />
            <ReconciliationRanges
                title={mergedAccountName}
                ranges={warning.sourceReconciliationRanges}
            />
            {lockedTransactions.length > 0 && (
                <div className="mt-2">
                    <p className="mb-0">
                        {lockedTransactionsNumber}{" "}
                        {pluralize("transaction", lockedTransactionsNumber)} in
                        the locked period can&apos;t move while the books are
                        locked. Merging removes the lock for this entity, then
                        moves them to <strong>{destinationAccountName}</strong>
                        {lockedTransactions.length < lockedTransactionsNumber
                            ? `, showing the first ${lockedTransactions.length}`
                            : ""}
                        :
                    </p>
                    <div
                        className={classNames(
                            gridTableStyles.gridTable,
                            tableStyles.table,
                            tableStyles.scrollableList,
                        )}
                    >
                        {lockedTransactions.map((transaction) => (
                            <TransactionToMerge
                                transaction={transaction}
                                key={transaction.id}
                            />
                        ))}
                    </div>
                </div>
            )}

            <HStack
                as="footer"
                justify="between"
                align="start"
                className="mt-4"
            >
                <Button variant="secondary" size="md" onClick={onCancel}>
                    Cancel
                </Button>
                <VStack gap={8} align="end">
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        onClick={onContinue}
                        loading={continuing}
                        setLoading={setContinuing}
                        disabled={isUnlockBlocked}
                    >
                        {requiresUnlock ? "Unlock and merge" : "Continue merge"}
                    </ButtonWithLoader>
                    {isUnlockBlocked && (
                        <p className="small mb-0">
                            Only someone who can edit locks can merge this
                            account
                        </p>
                    )}
                </VStack>
            </HStack>
        </StandardModalBody>
    );
};
